import * as React from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Home, RefreshCw } from 'lucide-react';
import { ApiError } from '../api/client';
import { Button } from '../ui/primitives';
import { t } from '../../i18n';

/**
 * Route-level error boundary.
 *
 * Distinguishes the three failures a user can actually act on — a plan gate,
 * a permission problem, and a missing page — from everything else, which gets
 * a retry and a request id to quote to support.
 */
/**
 * A lazy route chunk the browser could not fetch: the page was loaded before
 * the dev server restarted (or a new build was deployed) and now asks for a
 * file that no longer exists at that address. A reload fixes it; do it once,
 * automatically, so nobody has to know that.
 */
function isStaleChunk(error: unknown): boolean {
  return error instanceof Error && /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk/i.test(error.message);
}

export function RouteError({ notFound = false }: { notFound?: boolean }): React.JSX.Element {
  const error = useRouteError();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isStaleChunk(error)) return;
    const key = 'sk:chunk-reload';
    try {
      // One reload per URL — if the file is genuinely gone, show the screen.
      if (sessionStorage.getItem(key) === window.location.href) return;
      sessionStorage.setItem(key, window.location.href);
    } catch {
      // Storage blocked: reload anyway.
    }
    window.location.reload();
  }, [error]);

  let title = 'Something went wrong';
  let description = 'An unexpected error occurred. Try again, or contact support if it persists.';
  let requestId: string | undefined;
  let action: React.ReactNode = null;

  if (notFound || (isRouteErrorResponse(error) && error.status === 404)) {
    title = 'Page not found';
    description = 'That page does not exist, or you no longer have access to it.';
  } else if (error instanceof ApiError) {
    requestId = error.requestId;

    if (error.isPlanGate) {
      const requiredPlan = error.meta['requiredPlan'];
      title = 'Not included in your plan';
      description =
        typeof requiredPlan === 'string'
          ? `This feature is available on the ${requiredPlan} plan and above.`
          : error.message;
      action = <Button onClick={() => navigate('/subscription')}>{t('See plans')}</Button>;
    } else if (error.isReadOnly) {
      title = 'Your society is read-only';
      description = error.message;
      action = <Button onClick={() => navigate('/subscription')}>{t('Renew subscription')}</Button>;
    } else if (error.status === 403) {
      title = 'You do not have access';
      description = 'Ask your society admin to give you the role this page needs.';
    } else {
      description = error.message;
    }
  } else if (error instanceof Error) {
    // Only useful to a developer, and only in dev — a production stack trace in
    // the UI helps an attacker more than it helps the user.
    description = import.meta.env.DEV ? error.message : description;
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 flex size-14 items-center justify-center rounded-sk-2xl bg-danger-soft">
        <AlertTriangle className="size-6 text-danger-ink" aria-hidden />
      </div>

      <h1 className="font-display text-xl font-bold">{title}</h1>
      <p className="mt-1.5 max-w-md text-sm text-ink-2">{description}</p>

      {requestId ? (
        <p className="mt-3 font-mono text-2xs text-ink-3">
          {t('Reference:')}{' '}<span className="select-all">{requestId}</span>
        </p>
      ) : null}

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        {action}
        <Button variant="secondary" onClick={() => navigate(-1)}>
          <ArrowLeft className="size-4" aria-hidden />{' '}{t('Go back')}
        </Button>
        <Button variant="ghost" onClick={() => window.location.reload()}>
          <RefreshCw className="size-4" aria-hidden />{' '}{t('Retry')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          <Home className="size-4" aria-hidden />{' '}{t('Dashboard')}
        </Button>
      </div>
    </div>
  );
}

export default RouteError;
